'use client';

import { Suspense, useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { inventoryApi, partsApi, type Part } from '@parts/api';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Camera,
  QrCode,
  Package,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Maximize,
  RefreshCw,
} from 'lucide-react';

function ScanPageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { t, locale } = useTranslation();
  const [mode, setMode] = useState<'checkin' | 'checkout'>(
    (searchParams?.get('mode') as 'checkin' | 'checkout') || 'checkin'
  );
  const [scanning, setScanning] = useState(false);
  const [code, setCode] = useState('');
  const [part, setPart] = useState<Part | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [station, setStation] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  /**
   * 解析二维码内容
   * 支持两种格式：
   * 1. JSON 格式: {"pn":"PART-001","name":"Part Name","ts":"2024-01-01T00:00:00.000Z"}
   * 2. 纯零件号: PART-001
   */
  const parseQRCode = (qrData: string): string => {
    try {
      // 尝试解析 JSON 格式
      const data = JSON.parse(qrData);
      if (data.pn) {
        return data.pn; // 返回 Part Number
      }
    } catch {
      // 不是 JSON，直接返回原始内容作为零件号
    }
    return qrData.trim();
  };

  // 查询零件信息
  const handleCodeSubmit = async () => {
    if (!code.trim()) return;

    try {
      setLoading(true);
      // 解析二维码，获取零件号
      const partNumber = parseQRCode(code);
      const response = await partsApi.getByCode(partNumber);
      setPart(response);
      setResult(null);
    } catch (error) {
      console.error('Failed to find part:', error);
      setResult({
        success: false,
        message: t.inventory.invalidCode,
      });
      setPart(null);
    } finally {
      setLoading(false);
    }
  };

  // 执行入库/出库
  const handleSubmit = async () => {
    if (!part || !quantity) return;

    const qty = parseInt(quantity);
    if (qty <= 0) {
      setResult({
        success: false,
        message: t.common.invalidInput,
      });
      return;
    }

    try {
      setLoading(true);
      if (mode === 'checkin') {
        await inventoryApi.checkIn({
          code,
          quantity: qty,
          station,
          reason,
        });
        setResult({
          success: true,
          message: t.inventory.checkInSuccess,
        });
      } else {
        if (!station.trim()) {
          setResult({
            success: false,
            message: t.inventory.selectStation,
          });
          return;
        }
        await inventoryApi.checkOut({
          code,
          quantity: qty,
          station,
          reason,
        });
        setResult({
          success: true,
          message: t.inventory.checkOutSuccess,
        });
      }

      // 清空表单
      setTimeout(() => {
        setCode('');
        setPart(null);
        setQuantity('1');
        setStation('');
        setReason('');
        setResult(null);
      }, 2000);
    } catch (error: any) {
      setResult({
        success: false,
        message: error.response?.data?.message || t.common.error,
      });
    } finally {
      setLoading(false);
    }
  };

  // 切换全屏
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // 模拟扫码（实际应集成摄像头库）
  const handleStartScan = () => {
    setScanning(true);
    // 注意：这里需要集成实际的扫码库
    // 例如：html5-qrcode, react-webcam + jsQR
    // 示例代码：
    // import { Html5QrcodeScanner } from 'html5-qrcode';
    // const scanner = new Html5QrcodeScanner('reader', { fps: 10, qrbox: 250 });
    // scanner.render(onScanSuccess, onScanError);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push('/parts')}
                className="mr-4"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
                  {mode === 'checkin' ? t.scan.checkInTitle : t.scan.checkOutTitle}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFullscreen}
              >
                <Maximize className="w-4 h-4" />
              </Button>
              <div className="flex rounded-lg border border-gray-300 p-1">
                <button
                  onClick={() => setMode('checkin')}
                  className={`px-3 py-1 md:px-4 md:py-2 rounded text-sm font-medium transition-colors ${
                    mode === 'checkin'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t.inventory.checkIn}
                </button>
                <button
                  onClick={() => setMode('checkout')}
                  className={`px-3 py-1 md:px-4 md:py-2 rounded text-sm font-medium transition-colors ${
                    mode === 'checkout'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {t.inventory.checkOut}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Result Message */}
        {result && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-start ${
              result.success
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            {result.success ? (
              <CheckCircle className="w-6 h-6 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
            ) : (
              <XCircle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className={`font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                {result.message}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Scanner */}
          <div className="space-y-6">
            {/* Scan Area */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t.inventory.scanCode}
              </h2>

              {/* Camera View (Placeholder) */}
              <div className="aspect-square bg-gray-900 rounded-lg mb-4 flex items-center justify-center">
                {scanning ? (
                  <div className="text-center">
                    <Camera className="w-16 h-16 text-white mx-auto mb-4" />
                    <p className="text-white text-sm">{t.scan.scanCodePrompt}</p>
                    <p className="text-gray-400 text-xs mt-2">{t.scan.checkPermissions}</p>
                  </div>
                ) : (
                  <QrCode className="w-24 h-24 text-gray-600" />
                )}
              </div>

              <Button
                className="w-full mb-4 h-12 text-lg"
                onClick={handleStartScan}
                disabled={scanning}
              >
                {scanning ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    {t.scan.stopScanning}
                  </>
                ) : (
                  <>
                    <Camera className="w-5 h-5 mr-2" />
                    {t.scan.startScanning}
                  </>
                )}
              </Button>

              {/* Manual Input */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.common.or} {t.common.manualInput}
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder={t.inventory.scanPlaceholder}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
                    className="text-lg h-12"
                  />
                  <Button onClick={handleCodeSubmit} disabled={loading} className="h-12">
                    {loading ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <QrCode className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Part Info & Form */}
          <div className="space-y-6">
            {/* Part Information */}
            {part ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <Package className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t.scan.partInfo}
                    </h2>
                  </div>
                  <span
                    className={`px-3 py-1 text-sm font-medium rounded-full ${
                      part.currentStock === 0
                        ? 'bg-red-100 text-red-700'
                        : part.currentStock <= part.minStock
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {part.currentStock} {part.unit}
                  </span>
                </div>

                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm text-gray-500">{t.parts.partNumber}</dt>
                    <dd className="text-base font-medium text-gray-900">{part.partNumber}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">{t.parts.partNameLabel}</dt>
                    <dd className="text-base font-medium text-gray-900">
                      {locale === 'zh' ? (part.partNameCn || part.partNameEn) : part.partNameEn}
                    </dd>
                  </div>
                  {part.station && (
                    <div>
                      <dt className="text-sm text-gray-500">{t.parts.station}</dt>
                      <dd className="text-base text-gray-900">{part.station}</dd>
                    </div>
                  )}
                  {part.warehouseLocation && (
                    <div>
                      <dt className="text-sm text-gray-500">{t.parts.warehouseLocation}</dt>
                      <dd className="text-base text-gray-900">{part.warehouseLocation}</dd>
                    </div>
                  )}
                </dl>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <QrCode className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t.scan.scanCodePrompt}</p>
              </div>
            )}

            {/* Operation Form */}
            {part && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t.scan.operationDetails}
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.inventory.quantity} <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="text-lg h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.parts.station}
                      {mode === 'checkout' && <span className="text-red-500"> *</span>}
                    </label>
                    <Input
                      type="text"
                      value={station}
                      onChange={(e) => setStation(e.target.value)}
                      placeholder={t.inventory.selectStation}
                      className="text-lg h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.inventory.reason}
                    </label>
                    <Input
                      type="text"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t.inventory.enterReason}
                      className="text-lg h-12"
                    />
                  </div>

                  <Button
                    className="w-full h-14 text-lg"
                    onClick={handleSubmit}
                    disabled={loading || !quantity}
                  >
                    {loading ? (
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2" />
                    )}
                    {t.scan.confirmOperation}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ScanPage() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen">加载中...</div>}>
      <ScanPageContent />
    </Suspense>
  );
}

